/**
 * Requirement-scoped doc extraction schemas keyed to authoredSchema.
 *
 * Used when a doc carries `intendedRequirementIds` matching the
 * requirement. Fields land in SED `provenanceDetail.autoFillFields`
 * for Populate (doc tier).
 */
import { Type } from '../../services/geminiClient';
import { checklistRequirementIds } from '../../config/documentChecklists';
import { deriveRequirementDocExtractSchema } from './authoredSchemaToGeminiSchema';
import { attributionSchemaProps } from '../sed/evidenceAttribution';

// P4 — requirement extractions carry the same stated-provenance keys as the
// section schemas; `sanitizeRequirementExtractedFields` never persists them.
const ATTRIBUTION_SCHEMA_PROPS = attributionSchemaProps(Type.STRING);

export interface RequirementDocExtractSchema {
  requirementId: string;
  /** Gemini response schema — property keys match authoredSchema keys. */
  schema: Record<string, unknown>;
  required: string[];
}

export const REQUIREMENT_DOC_EXTRACT_SCHEMAS: RequirementDocExtractSchema[] = [
  {
    requirementId: 'flood-cert',
    schema: {
      type: Type.OBJECT,
      properties: {
        bfe_ft: { type: Type.NUMBER },
        floodZone: { type: Type.STRING },
        datum: { type: Type.STRING },
        topOfLowestFloor_ft: { type: Type.NUMBER },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: ['floodZone'],
  },
  {
    requirementId: 'utility-letter',
    schema: {
      type: Type.OBJECT,
      properties: {
        utilityName: { type: Type.STRING },
        serviceDate: { type: Type.STRING },
        capacityOffered_MW: { type: Type.NUMBER },
        nearestTransmissionKm: { type: Type.NUMBER, description: 'Source-stated distance to the nearest transmission corridor, in km; not capacity or a service commitment.' },
        substationDistKm: { type: Type.NUMBER, description: 'Source-stated distance to the nearest substation, in km; not utility-offered capacity.' },
        nearestPipelineKm: { type: Type.NUMBER, description: 'Source-stated distance to the nearest gas pipeline, in km; proximity does not establish deliverability.' },
        nearestPipelineOperator: { type: Type.STRING, description: 'Explicitly named operator of the nearest gas pipeline; omit when unknown.' },
        nearestPipelineMassFlowValue: { type: Type.NUMBER, description: 'Explicit pipeline quantity, paired with its stated unit and basis; do not infer available or firm site capacity.' },
        nearestPipelineMassFlowUnit: { type: Type.STRING, description: 'Unit stated for the pipeline quantity, e.g. Dth/day or MMSCFD; do not invent a conversion.' },
        nearestPipelineMassFlowBasis: { type: Type.STRING, description: 'Exactly one source-stated basis: indicative-throughput, firm site-deliverable capacity, contracted delivery point capacity, reserved metered capacity, committed site interconnect capacity. Omit if not stated; corridor throughput is not firm site delivery.' },
        interconnectionClass: { type: Type.STRING },
        costEstimate_USD: { type: Type.NUMBER },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: ['utilityName'],
  },
  {
    requirementId: 'geotech',
    schema: {
      type: Type.OBJECT,
      properties: {
        bearingCapacity_kPa: { type: Type.NUMBER },
        soilClassification: { type: Type.STRING },
        boreholeDepth_m: { type: Type.NUMBER },
        groundwaterDepth_m: { type: Type.NUMBER },
        preparedBy: { type: Type.STRING },
        reportDate: { type: Type.STRING },
        foundationRecommendation: { type: Type.STRING },
        settlementRisk: { type: Type.STRING },
        liquefactionRisk: { type: Type.STRING },
        expansiveSoilRisk: { type: Type.STRING },
        seismicSiteClass: { type: Type.STRING },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: ['soilClassification'],
  },
  {
    // Title / parcel survey — keys mirror the `parcel-survey` authoredSchema
    // (documentChecklists.ts) + the `compileCoverageAutoFillFromSed` parcel
    // autoFill key set, so a stamped extraction feeds the same coverage
    // fields the Author dialog writes. `parcelId` required: a title/parcel
    // survey without an APN/parcel reference is not a usable extraction —
    // keeps loose tag-matched docs from stamping noise onto the parcel slot.
    requirementId: 'parcel-survey',
    schema: {
      type: Type.OBJECT,
      properties: {
        parcelId: { type: Type.STRING },
        recordedAcreage: { type: Type.NUMBER },
        netUsableAcres: { type: Type.NUMBER, description: 'Source-stated net usable / buildable area in acres, separate from gross or recorded acreage; not an engineered capacity or fit result.' },
        deedRestrictions: { type: Type.STRING },
        easements: { type: Type.ARRAY, items: { type: Type.STRING } },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: ['parcelId'],
  },
  {
    requirementId: 'alta-survey',
    schema: {
      type: Type.OBJECT,
      properties: {
        standardYear: { type: Type.STRING },
        surveyor: { type: Type.STRING },
        surveyDate: { type: Type.STRING },
        surveyedAcreage: { type: Type.NUMBER },
        legalDescriptionConfirmed: { type: Type.BOOLEAN },
        accessConfirmed: { type: Type.BOOLEAN },
        easements: { type: Type.ARRAY, items: { type: Type.STRING } },
        rightsOfWay: { type: Type.ARRAY, items: { type: Type.STRING } },
        encroachments: { type: Type.ARRAY, items: { type: Type.STRING } },
        utilitiesObserved: { type: Type.ARRAY, items: { type: Type.STRING } },
        tableAItems: { type: Type.ARRAY, items: { type: Type.STRING } },
        floodZone: { type: Type.STRING },
        contoursIncluded: { type: Type.BOOLEAN },
        contourInterval_ft: { type: Type.NUMBER },
        verticalDatum: { type: Type.STRING },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: [],
  },
  {
    requirementId: 'mineral-title',
    schema: {
      type: Type.OBJECT,
      properties: {
        preparer: { type: Type.STRING },
        reportDate: { type: Type.STRING },
        countyRecordReference: { type: Type.STRING },
        ownershipStatus: { type: Type.STRING },
        reservedCommodities: { type: Type.ARRAY, items: { type: Type.STRING } },
        activeLeases: { type: Type.ARRAY, items: { type: Type.STRING } },
        surfaceUseRestrictions: { type: Type.ARRAY, items: { type: Type.STRING } },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
        excerptPreview: { type: Type.STRING },
        ...ATTRIBUTION_SCHEMA_PROPS,
      },
    },
    required: [],
  },
  {
    requirementId: 'archaeological-survey',
    schema: {
      type: Type.OBJECT,
      properties: {
        qualifiedProfessional: { type: Type.STRING },
        surveyDate: { type: Type.STRING },
        surveyLevel: { type: Type.STRING },
        acreageSurveyed: { type: Type.NUMBER },
        resourcesIdentified: { type: Type.BOOLEAN },
        resourceCount: { type: Type.NUMBER },
        eligibilityRecommendation: { type: Type.STRING },
        effectFinding: { type: Type.STRING },
        shpoCaseNumber: { type: Type.STRING },
        pageNumber: { type: Type.NUMBER },
        sectionHeading: { type: Type.STRING },
      },
    },
    required: [],
  },
];

export function getRequirementDocExtractSchema(
  requirementId: string,
): RequirementDocExtractSchema | undefined {
  // Hand-written specs win — they carry curated descriptions, required
  // anchors and (for archaeology) hard-learned redaction shape. The derived
  // fallback (D2) covers every OTHER requirement with an authoredSchema, so
  // "nobody transcribed the schema yet" stops meaning "extraction is off".
  return REQUIREMENT_DOC_EXTRACT_SCHEMAS.find(s => s.requirementId === requirementId)
    ?? deriveRequirementDocExtractSchema(requirementId);
}

/** Requirement ids with doc extraction: hand-written ∪ derived. */
export function requirementIdsWithDocExtract(): string[] {
  const ids = new Set(REQUIREMENT_DOC_EXTRACT_SCHEMAS.map(s => s.requirementId));
  for (const id of checklistRequirementIds()) {
    if (!ids.has(id) && deriveRequirementDocExtractSchema(id)) ids.add(id);
  }
  return [...ids];
}

/**
 * Stable fingerprint of a requirement's extraction schema — a cache-key
 * axis. When the schema gains/loses/retypes a property or changes its
 * required set, the fingerprint changes and prior cache entries stop
 * matching, so a schema edit can never serve results extracted under the
 * old shape. djb2 over a canonical serialization — cheap, deterministic,
 * no crypto import.
 */
export function requirementSchemaFingerprint(requirementId: string): string {
  const spec = getRequirementDocExtractSchema(requirementId);
  if (!spec) return 'none';
  const properties = (spec.schema as {
    properties?: Record<string, { type?: unknown; items?: { type?: unknown }; description?: string }>;
  }).properties ?? {};
  const props = Object.keys(properties).sort().map(key => {
    const p = properties[key];
    const item = p.items?.type ? `<${String(p.items.type)}>` : '';
    // Descriptions participate: for DERIVED schemas they carry the enum
    // values and units, so a checklist enumValues/unit edit must bust the
    // cache even though the property's key and type are unchanged.
    const desc = p.description ? `#${p.description}` : '';
    return `${key}:${String(p.type ?? '?')}${item}${desc}`;
  });
  const raw = `${props.join(',')}|req=${[...spec.required].sort().join(',')}`;
  let h = 5381;
  for (let i = 0; i < raw.length; i++) h = ((h << 5) + h + raw.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
